import { useCallback, useMemo } from 'react';
import {
  competitiveEstimate,
  equipmentComparisonItems,
  equipmentType,
  EstimateCheckListSpreadSheet,
  inHouseReview,
  ourProductUsageStatus,
  pastPerformance,
  paymentType,
  procurementApproval,
  reverseAuction,
  supplier,
  ValidDateRangeEnd,
  ValidDateRangeStart,
} from '../contexts/constants';
import { Currency, EstimateCheckListFormType, EquipmentComparison } from '../contexts/types';
import { useSpreadSheetSync, SpreadSheetSyncConfig } from './useSpreadSheetSync';
import GoogleAPI from '../utils/GoogleAPI';
import i18n from '../utils/i18n';
import { formatDateByLocale, sanitizeFileName } from '../utils/utils';

const T_PREFIX = 'components.EstimateCheckList';

/** 内製 */
const IN_HOUSE = "MMI内製";

/** シート行番号定義（No.キー → 行番号） */
export const ROW = {
  header1: 1,
  columnHeader: 2,
  currency: 3,
  exchangeRate: 4,
  sectionBasicCheck: 5,
  equipmentType: 6,
  reverseAuction: 7,
  inHouseReview: 8,
  inHouseReviewNoReason: 9,
  competitiveEstimate: 10,
  competitiveEstimateNoReason: 11,
  pastPerformance: 12,
  purchaseDate: 13,
  purchasePrice: 14,
  supplier: 15,
  orderDate: 16,
  distributorReason: 17,
  negotiationCount: 18,
  negotiationBackground: 19,
  paymentType: 20,
  paymentBackground: 21,
  procurementApproval: 22,
  ourProductUsage: 23,
  ourProductUsageProductName: 24,
  purchaseWithoutOutsourcing: 25,
  sectionComparisonHeader: 27,
  companyName: 28,
  basePrice: 29,
  unit: 30,
  optionPrice: 31,
  features: 32,
  totalPrice: 33,
  jpyAmount: 34,
  deadline: 35,
  quality: 36,
  system: 37,
  repeat: 38,
  issue: 39,
  sectionSelectReason: 41,
  selectReason: 42,
} as const;

/** No.キー定義（行番号 → No.キー、読み取り時の存在検証に使用） */
export const NO_KEY: Record<number, string> = {
  [ROW.currency]: "H-2",
  [ROW.exchangeRate]: "H-3",
  [ROW.equipmentType]: "L-1",
  [ROW.reverseAuction]: "L-2",
  [ROW.inHouseReview]: "L-3",
  [ROW.inHouseReviewNoReason]: "L-3-1",
  [ROW.competitiveEstimate]: "L-4",
  [ROW.competitiveEstimateNoReason]: "L-4-1",
  [ROW.pastPerformance]: "L-5",
  [ROW.purchaseDate]: "L-5-1",
  [ROW.purchasePrice]: "L-5-2",
  [ROW.supplier]: "L-6",
  [ROW.orderDate]: "L-6-1",
  [ROW.distributorReason]: "L-6-2",
  [ROW.negotiationCount]: "L-7",
  [ROW.negotiationBackground]: "L-7-1",
  [ROW.paymentType]: "L-8",
  [ROW.paymentBackground]: "L-8-1",
  [ROW.procurementApproval]: "L-8-2",
  [ROW.ourProductUsage]: "L-9",
  [ROW.ourProductUsageProductName]: "L-9-1",
  [ROW.purchaseWithoutOutsourcing]: "L-10",
  [ROW.companyName]: "R-0",
  [ROW.basePrice]: "R-6",
  [ROW.unit]: "R-7",
  [ROW.optionPrice]: "R-8",
  [ROW.features]: "R-9",
  [ROW.totalPrice]: "R-10",
  [ROW.jpyAmount]: "R-11",
  [ROW.deadline]: "R-12",
  [ROW.quality]: "R-13",
  [ROW.system]: "R-14",
  [ROW.repeat]: "R-15",
  [ROW.issue]: "R-16",
  [ROW.selectReason]: "S-1",
};

/** 列番号定義（1始まり、A=1） */
export const COL = { no: 1, item: 2, value: 3, note: 4, company2: 4, company3: 5, superiority: 6 } as const;

/** 最終行 */
const LAST_ROW = ROW.selectReason;

/**
 * 見積チェックリスト＋比較表企業一覧に対する入力値
 */
export interface EstimateCheckListSpreadSheetData {
  formValue: EstimateCheckListFormType;
  currencyData: Currency[];
}

/**
 * 見積チェックリスト データ連携結果
 */
export interface EstimateCheckListConversionResult {
  formValue: EstimateCheckListFormType | null;
  /** 読み取り時に検出したエラー（構造エラー・業務エラー） */
  errors: string[];
}

/**
 * ラジオ/セレクト系のname→value逆変換（表示言語のname、または保存値valueのどちらでも受け付ける）
 */
const resolveValue = (displayValue: string, options: { value: string; name: string }[]): string | null => {
  const trimmed = (displayValue ?? "").trim();
  if (!trimmed) return null;
  const found = options.find(opt => opt.name === trimmed || opt.value === trimmed);
  return found ? found.value : null;
};

/** 数値文字列からカンマを除去してnumberへ変換（空はnull） */
const parseNumberCell = (cell: any): number | null => {
  if (cell === null || cell === undefined || cell === "") return null;
  const cleaned = String(cell).replace(/,/g, "").trim();
  if (cleaned === "") return null;
  const num = Number(cleaned);
  return isNaN(num) ? null : num;
};

/** 数値セル→文字列（EquipmentComparisonの型がstringのため） */
const parseNumberCellToString = (cell: any): string => {
  const num = parseNumberCell(cell);
  return num === null ? "" : String(num);
};

/**
 * 見積チェックリストデータをSpreadSheetの2次元配列に変換する。
 * A〜F列、1〜42行の帳票フォーム型レイアウト（対応方針§3参照）。
 */
export const estimateCheckListToSpreadSheetData = (
  data: EstimateCheckListSpreadSheetData,
  equipmentName: string
): any[][] => {
  const { formValue } = data;
  void data.currencyData; // 生成時点の通貨マスタはonAfterWrite側（プルダウン設定）で使用する
  const t = (key: string, options?: any) => i18n.t(`${T_PREFIX}.sheet.${key}`, options);

  // 行配列を作成（1始まりの行番号に合わせるためインデックス0はダミー）
  const rows: any[][] = Array.from({ length: LAST_ROW + 1 }, () => ["", "", "", "", "", ""]);

  rows[ROW.header1] = [t('header1'), "", t('header2', { equipmentName }), t('headerNote'), "", ""];
  rows[ROW.columnHeader] = [t('colNo'), t('colItem'), t('colValue'), t('colNote'), "", ""];

  rows[ROW.currency] = ["H-2", EstimateCheckListCategoryLabel('currency'), formValue?.currency?.currencyName ?? "JPY", t('noteCurrency'), "", ""];
  rows[ROW.exchangeRate] = ["H-3", EstimateCheckListCategoryLabel('exchangeRate'), formValue?.currency?.exchangeRate ?? 1, t('noteExchangeRate'), "", ""];

  rows[ROW.sectionBasicCheck] = [t('sectionBasicCheck'), "", "", "", "", ""];

  rows[ROW.equipmentType] = ["L-1", equipmentTypeLabel(), formValue?.equipmentType?.equipmentType === equipmentType.dedicatedEquipment.value ? equipmentType.dedicatedEquipment.name : equipmentType.genericEquipment.name, t('noteEquipmentType'), "", ""];
  rows[ROW.reverseAuction] = ["L-2", reverseAuctionLabel(), formValue?.reverseAuction?.reverseAuction === reverseAuction.isFalse.value ? reverseAuction.isFalse.name : reverseAuction.isTrue.name, t('noteReverseAuction'), "", ""];
  rows[ROW.inHouseReview] = ["L-3", inHouseReviewLabel(), formValue?.inHouseReview?.inHouseReview === inHouseReview.isFalse.value ? inHouseReview.isFalse.name : inHouseReview.isTrue.name, t('noteInHouseReview'), "", ""];
  rows[ROW.inHouseReviewNoReason] = ["L-3-1", inHouseReviewNoReasonLabel(), formValue?.inHouseReview?.inHouseReviewNoReason ?? "", t('noteInHouseReviewNoReason'), "", ""];
  rows[ROW.competitiveEstimate] = ["L-4", competitiveEstimateLabel(), formValue?.competitiveEstimate?.competitiveEstimate === competitiveEstimate.isFalse.value ? competitiveEstimate.isFalse.name : competitiveEstimate.isTrue.name, t('noteCompetitiveEstimate'), "", ""];
  rows[ROW.competitiveEstimateNoReason] = ["L-4-1", competitiveEstimateNoReasonLabel(), formValue?.competitiveEstimate?.competitiveEstimateNoReason ?? "", t('noteCompetitiveEstimateNoReason'), "", ""];
  rows[ROW.pastPerformance] = ["L-5", pastPerformanceLabel(), formValue?.pastPerformance?.pastPerformance === pastPerformance.isFalse.value ? pastPerformance.isFalse.name : pastPerformance.isTrue.name, t('notePastPerformance'), "", ""];
  rows[ROW.purchaseDate] = ["L-5-1", purchaseDateLabel(), formValue?.pastPerformance?.purchaseDate ?? "", t('notePurchaseDate', { start: formatDateByLocale(ValidDateRangeStart), end: formatDateByLocale(ValidDateRangeEnd) }), "", ""];
  rows[ROW.purchasePrice] = ["L-5-2", purchasePriceLabel(), formValue?.pastPerformance?.purchasePrice ?? "", t('notePurchasePrice'), "", ""];
  rows[ROW.supplier] = ["L-6", supplierLabel(), formValue?.supplier?.supplier === supplier.distributor.value ? supplier.distributor.name : supplier.manufacturer.name, t('noteSupplier'), "", ""];
  rows[ROW.orderDate] = ["L-6-1", orderDateLabel(), formValue?.supplier?.orderDate ?? "", t('noteOrderDate', { start: formatDateByLocale(ValidDateRangeStart), end: formatDateByLocale(ValidDateRangeEnd) }), "", ""];
  rows[ROW.distributorReason] = ["L-6-2", distributorReasonLabel(), formValue?.supplier?.distributorReason ?? "", t('noteDistributorReason'), "", ""];
  rows[ROW.negotiationCount] = ["L-7", negotiationLabel(), formValue?.negotiation?.negotiationCount ?? "", t('noteNegotiationCount'), "", ""];
  rows[ROW.negotiationBackground] = ["L-7-1", negotiationBackgroundLabel(), formValue?.negotiation?.negotiationBackground ?? "", t('noteNegotiationBackground'), "", ""];
  rows[ROW.paymentType] = ["L-8", paymentTypeLabel(), formValue?.paymentType?.advancePayment === paymentType.isFalse.value ? paymentType.isFalse.name : paymentType.isTrue.name, t('notePaymentType'), "", ""];
  rows[ROW.paymentBackground] = ["L-8-1", paymentBackgroundLabel(), formValue?.paymentType?.paymentBackground ?? "", t('notePaymentBackground'), "", ""];
  rows[ROW.procurementApproval] = ["L-8-2", i18n.t('common.constants.estimateCheckListCategory.procurementApproval', { defaultValue: '調達物流部門担当役員の承認' }), formValue?.paymentType?.procurementApproval === procurementApproval.isFalse.value ? procurementApproval.isFalse.name : procurementApproval.isTrue.name, t('noteProcurementApproval'), "", ""];
  rows[ROW.ourProductUsage] = ["L-9", ourProductUsageLabel(), resolveOurProductUsageName(formValue), t('noteOurProductUsage'), "", ""];
  rows[ROW.ourProductUsageProductName] = ["L-9-1", i18n.t('common.constants.estimateCheckListCategory.productName', { defaultValue: '使用製品名' }), formValue?.ourProductUsage?.productName ?? "", t('noteProductName'), "", ""];
  rows[ROW.purchaseWithoutOutsourcing] = ["L-10", purchaseWithoutOutsourcingLabel(), formValue?.purchaseWithoutOutsourcing?.reason ?? "", t('notePurchaseWithoutOutsourcing'), "", ""];

  rows[26] = ["", "", "", "", "", ""];

  const companies = formValue?.equipmentComparison?.equipmentComparison ?? [];
  const companyNameAt = (i: number) => {
    const c = companies[i];
    if (!c) return "";
    return c.companyName === IN_HOUSE ? i18n.t(`${T_PREFIX}.label.inHouseCompanyName`, { defaultValue: 'MMI内製' }) : c.companyName;
  };

  rows[ROW.sectionComparisonHeader] = [t('sectionComparison'), "", t('colCompany1'), t('colCompany2'), t('colCompany3'), t('colSuperiority')];
  rows[ROW.companyName] = ["R-0", t('rowLabelCompanyName'), companyNameAt(0), companyNameAt(1), companyNameAt(2), ""];

  const superiority = formValue?.equipmentComparison?.superiority ?? [];
  const superiorityLabelAt = (idx: number) => {
    const val = superiority[idx];
    if (!val || val === "-") return "-";
    return val;
  };

  const comparisonRow = (
    noKey: string,
    rowLabelKey: string,
    fieldGetter: (c: EquipmentComparison | undefined) => any,
    superiorityIndex: number
  ): any[] => [
      noKey,
      t(rowLabelKey),
      fieldGetter(companies[0]),
      fieldGetter(companies[1]),
      fieldGetter(companies[2]),
      superiorityLabelAt(superiorityIndex),
    ];

  rows[ROW.basePrice] = comparisonRow("R-6", "rowLabelBasePrice", c => c?.basePrice ?? "", 0);
  rows[ROW.unit] = comparisonRow("R-7", "rowLabelUnit", c => c?.unit ?? "", 1);
  rows[ROW.optionPrice] = comparisonRow("R-8", "rowLabelOptionPrice", c => c?.optionPrice ?? "", 2);
  rows[ROW.features] = comparisonRow("R-9", "rowLabelFeatures", c => c?.features ?? "", 3);
  rows[ROW.totalPrice] = comparisonRow("R-10", "rowLabelTotalPrice", c => c?.totalPrice ?? "", 4);
  rows[ROW.jpyAmount] = ["R-11", t('rowLabelJpyAmount'), "", "", "", ""]; // 数式で後から設定
  rows[ROW.deadline] = comparisonRow("R-12", "rowLabelDeadline", c => c?.deadline ?? "", 5);
  rows[ROW.quality] = comparisonRow("R-13", "rowLabelQuality", c => qualityName(c?.quality), 6);
  rows[ROW.system] = comparisonRow("R-14", "rowLabelSystem", c => systemName(c?.system), 7);
  rows[ROW.repeat] = comparisonRow("R-15", "rowLabelRepeat", c => repeatName(c?.repeat), 8);
  rows[ROW.issue] = comparisonRow("R-16", "rowLabelIssue", c => c?.issue ?? "", 9);

  rows[40] = ["", "", "", "", "", ""];
  rows[ROW.sectionSelectReason] = [t('sectionSelectReason'), "", "", "", "", ""];
  rows[ROW.selectReason] = ["S-1", i18n.t('common.constants.estimateCheckListCategory.selectReason', { defaultValue: '選定理由' }), formValue?.selectReason?.selectReason ?? "", t('noteSelectReason'), "", ""];

  // 行0（存在しないダミー行）を除去して1行目から返す
  return rows.slice(1);
};

// ラベル取得ヘルパー（i18n定義の共通ラベルを利用）
const EstimateCheckListCategoryLabel = (key: "currency" | "exchangeRate") =>
  key === "currency"
    ? i18n.t(`${T_PREFIX}.label.currency`, { defaultValue: '通貨' }).replace(/[:：]\s*$/, "")
    : i18n.t(`${T_PREFIX}.category.orderDate`, { defaultValue: '為替レート' });
const equipmentTypeLabel = () => i18n.t('common.constants.estimateCheckListCategory.equipmentType', { defaultValue: '設備種類' });
const reverseAuctionLabel = () => i18n.t('common.constants.estimateCheckListCategory.reverseAuction', { defaultValue: 'リバースオークション有無' });
const inHouseReviewLabel = () => i18n.t('common.constants.estimateCheckListCategory.inHouseReview', { defaultValue: '内製検討' });
const inHouseReviewNoReasonLabel = () => i18n.t('common.constants.estimateCheckListCategory.inHouseReviewNoReason', { defaultValue: '内製検討なしの理由' });
const competitiveEstimateLabel = () => i18n.t('common.constants.estimateCheckListCategory.competitiveEstimate', { defaultValue: '相見積' });
const competitiveEstimateNoReasonLabel = () => i18n.t('common.constants.estimateCheckListCategory.competitiveEstimateNoReason', { defaultValue: '相見積なしの理由' });
const pastPerformanceLabel = () => i18n.t('common.constants.estimateCheckListCategory.pastPerformance', { defaultValue: '過去実績' });
const purchaseDateLabel = () => i18n.t('common.constants.estimateCheckListCategory.purchaseDate', { defaultValue: '直近購入時期' });
const purchasePriceLabel = () => i18n.t('common.constants.estimateCheckListCategory.purchasePrice', { defaultValue: '取得単価' });
const supplierLabel = () => i18n.t('common.constants.estimateCheckListCategory.supplier', { defaultValue: '発注先' });
const orderDateLabel = () => i18n.t('common.constants.estimateCheckListCategory.orderDate', { defaultValue: '発注時期' });
const distributorReasonLabel = () => i18n.t('common.constants.estimateCheckListCategory.distributorReason', { defaultValue: '商社・代理店の理由' });
const negotiationLabel = () => i18n.t('common.constants.estimateCheckListCategory.negotiation', { defaultValue: '単価交渉の回数' });
const negotiationBackgroundLabel = () => i18n.t('common.constants.estimateCheckListCategory.negotiationBackground', { defaultValue: '単価交渉の経緯' });
const paymentTypeLabel = () => i18n.t('common.constants.estimateCheckListCategory.paymentType', { defaultValue: '支払条件' });
const paymentBackgroundLabel = () => i18n.t('common.constants.estimateCheckListCategory.paymentBackground', { defaultValue: '支払条件の経緯' });
const ourProductUsageLabel = () => i18n.t('common.constants.estimateCheckListCategory.ourProductUsage', { defaultValue: '当社製品使用の確認/要請' });
const purchaseWithoutOutsourcingLabel = () => i18n.t('common.constants.estimateCheckListCategory.purchaseWithoutOutsourcing', { defaultValue: '外注を使用せず購入する理由' });

const resolveOurProductUsageName = (formValue: EstimateCheckListFormType): string => {
  const status = formValue?.ourProductUsage?.usageStatus ?? ourProductUsageStatus.used.value;
  if (status === ourProductUsageStatus.otherCompanyUsed.value) return ourProductUsageStatus.otherCompanyUsed.name;
  if (status === ourProductUsageStatus.notApplicable.value) return ourProductUsageStatus.notApplicable.name;
  return ourProductUsageStatus.used.name;
};

const qualityName = (value?: string) => (Object.values(equipmentComparisonItems.quality) as { value: string; name: string }[]).find(v => v.value === value)?.name ?? equipmentComparisonItems.quality.none.name;
const systemName = (value?: string) => (Object.values(equipmentComparisonItems.system) as { value: string; name: string }[]).find(v => v.value === value)?.name ?? equipmentComparisonItems.system.none.name;
const repeatName = (value?: string) => (Object.values(equipmentComparisonItems.repeat) as { value: string; name: string }[]).find(v => v.value === value)?.name ?? equipmentComparisonItems.repeat.none.name;

/**
 * スプレッドシートから読み取った値を見積チェックリストデータへ変換する。
 * A列のNo.キーで項目を特定するため行位置には依存しない（行挿入・並び替え耐性）。
 * @param values spreadSheetGetValuesで取得した2次元配列（A1起点）
 * @param existingFormValue 既存のフォーム値（会社追加/削除時のデフォルト値補完に使用）
 * @param currencyData 通貨マスタ
 */
export const spreadSheetDataToEstimateCheckList = (
  values: any[][],
  existingFormValue: EstimateCheckListFormType,
  currencyData: Currency[]
): EstimateCheckListConversionResult => {
  const errors: string[] = [];
  const t = (key: string, options?: any): string => i18n.t(`${T_PREFIX}.message.${key}`, options) as string;

  // A列のNo.キー → 行データのマップを作成
  const rowByNoKey = new Map<string, any[]>();
  values.forEach((row) => {
    const noKey = (row?.[0] ?? "").toString().trim();
    if (noKey) rowByNoKey.set(noKey, row);
  });

  // 構造バリデーション：必須No.キーの存在確認
  const requiredKeys = Object.values(NO_KEY);
  const missingKeys = requiredKeys.filter(key => !rowByNoKey.has(key));
  if (missingKeys.length > 0) {
    errors.push(t('syncStructureError'));
    return { formValue: null, errors };
  }

  const cellValue = (noKey: string, col: number): any => rowByNoKey.get(noKey)?.[col - 1] ?? "";

  const formValue: EstimateCheckListFormType = JSON.parse(JSON.stringify(existingFormValue));

  // 通貨
  const currencyNameCell = String(cellValue("H-2", COL.value) ?? "").trim();
  const matchedCurrency = currencyData.find(c => c.currencyName === currencyNameCell);
  if (matchedCurrency) {
    formValue.currency = {
      currencyId: matchedCurrency.currencyId ?? null,
      currencyName: matchedCurrency.currencyName ?? null,
      exchangeRate: matchedCurrency.exchangeRate ?? 1,
    };
  } else {
    formValue.currency = { currencyId: null, currencyName: null, exchangeRate: null };
    errors.push(i18n.t(`${T_PREFIX}.category.equipmentType`, { defaultValue: '通貨' }) + ": " + t('required', {}));
  }

  // 設備種類
  formValue.equipmentType.equipmentType = resolveValue(String(cellValue("L-1", COL.value)), Object.values(equipmentType)) ?? formValue.equipmentType.equipmentType;
  // リバースオークション
  formValue.reverseAuction.reverseAuction = resolveValue(String(cellValue("L-2", COL.value)), Object.values(reverseAuction)) ?? formValue.reverseAuction.reverseAuction;
  // 内製検討
  formValue.inHouseReview.inHouseReview = resolveValue(String(cellValue("L-3", COL.value)), Object.values(inHouseReview)) ?? formValue.inHouseReview.inHouseReview;
  formValue.inHouseReview.inHouseReviewNoReason = toNullableString(cellValue("L-3-1", COL.value));
  // 相見積
  formValue.competitiveEstimate.competitiveEstimate = resolveValue(String(cellValue("L-4", COL.value)), Object.values(competitiveEstimate)) ?? formValue.competitiveEstimate.competitiveEstimate;
  formValue.competitiveEstimate.competitiveEstimateNoReason = toNullableString(cellValue("L-4-1", COL.value));
  // 過去実績
  formValue.pastPerformance.pastPerformance = resolveValue(String(cellValue("L-5", COL.value)), Object.values(pastPerformance)) ?? formValue.pastPerformance.pastPerformance;
  formValue.pastPerformance.purchaseDate = toNullableString(cellValue("L-5-1", COL.value));
  formValue.pastPerformance.purchasePrice = parseNumberCell(cellValue("L-5-2", COL.value));
  // 発注先
  formValue.supplier.supplier = resolveValue(String(cellValue("L-6", COL.value)), Object.values(supplier)) ?? formValue.supplier.supplier;
  formValue.supplier.orderDate = toNullableString(cellValue("L-6-1", COL.value));
  formValue.supplier.distributorReason = toNullableString(cellValue("L-6-2", COL.value));
  // 価格交渉
  formValue.negotiation.negotiationCount = parseNumberCell(cellValue("L-7", COL.value));
  formValue.negotiation.negotiationBackground = toNullableString(cellValue("L-7-1", COL.value));
  // 支払条件
  formValue.paymentType.advancePayment = resolveValue(String(cellValue("L-8", COL.value)), Object.values(paymentType)) ?? formValue.paymentType.advancePayment;
  formValue.paymentType.paymentBackground = toNullableString(cellValue("L-8-1", COL.value));
  formValue.paymentType.procurementApproval = resolveValue(String(cellValue("L-8-2", COL.value)), Object.values(procurementApproval));
  // 当社製品使用
  const usageResolved = resolveValue(String(cellValue("L-9", COL.value)), Object.values(ourProductUsageStatus));
  if (!formValue.ourProductUsage) formValue.ourProductUsage = { usageStatus: ourProductUsageStatus.used.value, productName: null };
  formValue.ourProductUsage.usageStatus = usageResolved ?? formValue.ourProductUsage.usageStatus;
  formValue.ourProductUsage.productName = toNullableString(cellValue("L-9-1", COL.value));
  // 外注を使用せず購入する理由
  if (!formValue.purchaseWithoutOutsourcing) formValue.purchaseWithoutOutsourcing = { reason: null };
  formValue.purchaseWithoutOutsourcing.reason = toNullableString(cellValue("L-10", COL.value));

  // 相見積による比較（会社名が空の列はスキップ）
  const companyNameCells = [cellValue("R-0", COL.value), cellValue("R-0", COL.company2), cellValue("R-0", COL.company3)]
    .map(v => String(v ?? "").trim());
  const inHouseLabel = i18n.t(`${T_PREFIX}.label.inHouseCompanyName`, { defaultValue: 'MMI内製' });
  const resolvedCompanyNames = companyNameCells.map(name => (name === inHouseLabel ? IN_HOUSE : name));

  // 重複会社名チェック
  const nonEmptyNames = resolvedCompanyNames.filter(n => n !== "");
  if (new Set(nonEmptyNames).size !== nonEmptyNames.length) {
    errors.push(i18n.t(`${T_PREFIX}.message.duplicateCompany`));
  }

  const readComparisonField = (noKey: string, colIndex: number): string => {
    const raw = cellValue(noKey, colIndex);
    return raw === null || raw === undefined ? "" : String(raw).trim();
  };

  const comparisons: EquipmentComparison[] = [];
  resolvedCompanyNames.forEach((companyName, idx) => {
    if (!companyName) return;
    const colIndex = idx === 0 ? COL.value : idx === 1 ? COL.company2 : COL.company3;
    comparisons.push({
      companyName,
      basePrice: parseNumberCellToString(cellValue("R-6", colIndex)),
      unit: parseNumberCellToString(cellValue("R-7", colIndex)),
      optionPrice: parseNumberCellToString(cellValue("R-8", colIndex)),
      features: readComparisonField("R-9", colIndex),
      totalPrice: parseNumberCellToString(cellValue("R-10", colIndex)),
      deadline: parseNumberCellToString(cellValue("R-12", colIndex)),
      quality: resolveValue(readComparisonField("R-13", colIndex), Object.values(equipmentComparisonItems.quality)) ?? equipmentComparisonItems.quality.none.value,
      system: resolveValue(readComparisonField("R-14", colIndex), Object.values(equipmentComparisonItems.system)) ?? equipmentComparisonItems.system.none.value,
      repeat: resolveValue(readComparisonField("R-15", colIndex), Object.values(equipmentComparisonItems.repeat)) ?? equipmentComparisonItems.repeat.none.value,
      issue: readComparisonField("R-16", colIndex),
    });
  });

  // 優位性（会社名→"{会社名}が優位"に変換。会社が1社以下ならすべて"-"）
  const superiorityNoKeys = ["R-6", "R-7", "R-8", "R-9", "R-10", "R-12", "R-13", "R-14", "R-15", "R-16"];
  const superiority = superiorityNoKeys.map((noKey) => {
    if (comparisons.length <= 1) return "-";
    const raw = String(cellValue(noKey, COL.superiority) ?? "").trim();
    if (!raw || raw === "-") return "-";
    const matchedName = resolvedCompanyNames.find(name => {
      const label = name === IN_HOUSE ? inHouseLabel : name;
      return raw === label;
    });
    if (!matchedName) {
      errors.push(i18n.t(`${T_PREFIX}.message.superiorityCompanyNotFound`));
      return "-";
    }
    return `${matchedName}が優位`;
  });

  formValue.equipmentComparison = {
    equipmentComparison: comparisons,
    superiority,
  };

  // 選定理由
  formValue.selectReason.selectReason = toNullableString(cellValue("S-1", COL.value));

  return { formValue, errors };
};

/** 空文字はnullとして扱う */
const toNullableString = (value: any): string | null => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  return str === "" ? null : str;
};

/**
 * 見積チェックリスト SpreadSheet同期用カスタムフック
 */
export const useEstimateCheckListSpreadSheet = (
  googleAPI: GoogleAPI,
  toast: (options: { title: string; description: React.ReactNode; status: 'error' | 'warning' | 'success' | 'info'; duration: number; isClosable: boolean }) => void,
  params: {
    projectId: string;
    equipmentId: string;
    equipmentName: string;
    currencyData: Currency[];
  }
) => {
  const fileName = useMemo(
    () => i18n.t(`${T_PREFIX}.sheet.fileName`, { projectId: params.projectId, equipmentName: sanitizeFileName(params.equipmentName ?? "") }),
    [params.projectId, params.equipmentName]
  );

  /**
   * 値書き込み後の書式・入力規則・条件付き書式・保護範囲を設定する。
   */
  const onAfterWrite = useCallback(async (spreadsheetId: string, _unusedSheetId: number) => {
    // Masterシート追加＋並び替え（先頭シートをSheetDataに固定するため）
    await googleAPI.addSheets(spreadsheetId, ["Master"]);
    await googleAPI.reorderSheets(spreadsheetId, [GoogleAPI.DEFAULT_SHEET_NAME, "Master"]);

    const sheetId = await googleAPI.getFirstSheetId(spreadsheetId) ?? 0;
    const spreadsheetInfo = await googleAPI.getSpreadSheetInfo(spreadsheetId);
    const masterSheet = (spreadsheetInfo.sheets ?? []).find((s: any) => s.properties?.title === "Master");
    const masterSheetId = masterSheet?.properties?.sheetId;

    await googleAPI.clearAllDataValidation(spreadsheetId);

    // Masterシートに通貨マスタを書き込み、C4に為替レート参照数式を設定
    if (masterSheetId !== undefined) {
      const masterRows = params.currencyData.map(c => [c.currencyName, c.exchangeRate]);
      await googleAPI.spreadSheetSetValue(spreadsheetId, "Master", 1, 1, masterRows.length > 0 ? masterRows : [["JPY", 1]]);
      await googleAPI.hideSheet(spreadsheetId, masterSheetId);
    }

    await googleAPI.spreadSheetSetFormulas(spreadsheetId, [
      { sheetId, row: ROW.exchangeRate, col: COL.value, formula: `=IFERROR(VLOOKUP($C$${ROW.currency},Master!$A:$B,2,FALSE),"")` },
      { sheetId, row: ROW.jpyAmount, col: COL.value, formula: `=IF(C${ROW.totalPrice}="","",C${ROW.totalPrice}*$C$${ROW.exchangeRate})` },
      { sheetId, row: ROW.jpyAmount, col: COL.company2, formula: `=IF(D${ROW.totalPrice}="","",D${ROW.totalPrice}*$C$${ROW.exchangeRate})` },
      { sheetId, row: ROW.jpyAmount, col: COL.company3, formula: `=IF(E${ROW.totalPrice}="","",E${ROW.totalPrice}*$C$${ROW.exchangeRate})` },
    ]);

    // 列幅
    await googleAPI.setColumnWidth(spreadsheetId, sheetId, 0, 1, 70);
    await googleAPI.setColumnWidth(spreadsheetId, sheetId, 1, 2, 300);
    await googleAPI.setColumnWidth(spreadsheetId, sheetId, 2, 3, 380);
    await googleAPI.setColumnWidth(spreadsheetId, sheetId, 3, 4, 320);
    await googleAPI.setColumnWidth(spreadsheetId, sheetId, 4, 5, 320);
    await googleAPI.setColumnWidth(spreadsheetId, sheetId, 5, 6, 170);

    // 自由記述行の折返し・行高
    await googleAPI.spreadSheetSetCellFormat(spreadsheetId, [
      { sheetId, startRow: ROW.inHouseReviewNoReason, endRow: ROW.inHouseReviewNoReason, startCol: COL.value, endCol: COL.value, format: { wrapStrategy: "WRAP" } },
      { sheetId, startRow: ROW.competitiveEstimateNoReason, endRow: ROW.competitiveEstimateNoReason, startCol: COL.value, endCol: COL.value, format: { wrapStrategy: "WRAP" } },
      { sheetId, startRow: ROW.distributorReason, endRow: ROW.distributorReason, startCol: COL.value, endCol: COL.value, format: { wrapStrategy: "WRAP" } },
      { sheetId, startRow: ROW.negotiationBackground, endRow: ROW.negotiationBackground, startCol: COL.value, endCol: COL.value, format: { wrapStrategy: "WRAP" } },
      { sheetId, startRow: ROW.paymentBackground, endRow: ROW.paymentBackground, startCol: COL.value, endCol: COL.value, format: { wrapStrategy: "WRAP" } },
      { sheetId, startRow: ROW.ourProductUsageProductName, endRow: ROW.ourProductUsageProductName, startCol: COL.value, endCol: COL.value, format: { wrapStrategy: "WRAP" } },
      { sheetId, startRow: ROW.purchaseWithoutOutsourcing, endRow: ROW.purchaseWithoutOutsourcing, startCol: COL.value, endCol: COL.value, format: { wrapStrategy: "WRAP" } },
      { sheetId, startRow: ROW.selectReason, endRow: ROW.selectReason, startCol: COL.value, endCol: COL.value, format: { wrapStrategy: "WRAP" } },
      // 補足列は折返し（基本チェック項目ブロックと選定理由のみ。比較表ブロックのD列は「会社2」の入力列のため対象外）
      { sheetId, startRow: ROW.currency, endRow: ROW.purchaseWithoutOutsourcing, startCol: COL.note, endCol: COL.note, format: { wrapStrategy: "WRAP" } },
      { sheetId, startRow: ROW.sectionSelectReason, endRow: ROW.selectReason, startCol: COL.note, endCol: COL.note, format: { wrapStrategy: "WRAP" } },
      // 見出し・区切り行は太字
      { sheetId, startRow: ROW.header1, endRow: ROW.header1, startCol: 1, endCol: 6, format: { textFormat: { bold: true } } },
      { sheetId, startRow: ROW.columnHeader, endRow: ROW.columnHeader, startCol: 1, endCol: 6, format: { textFormat: { bold: true }, backgroundColor: { red: 0.93, green: 0.93, blue: 0.93 } } },
      { sheetId, startRow: ROW.sectionBasicCheck, endRow: ROW.sectionBasicCheck, startCol: 1, endCol: 6, format: { textFormat: { bold: true }, backgroundColor: { red: 0.93, green: 0.93, blue: 0.93 } } },
      { sheetId, startRow: ROW.sectionComparisonHeader, endRow: ROW.sectionComparisonHeader, startCol: 1, endCol: 6, format: { textFormat: { bold: true }, backgroundColor: { red: 0.93, green: 0.93, blue: 0.93 } } },
      { sheetId, startRow: ROW.sectionSelectReason, endRow: ROW.sectionSelectReason, startCol: 1, endCol: 6, format: { textFormat: { bold: true }, backgroundColor: { red: 0.93, green: 0.93, blue: 0.93 } } },
      // 編集不可セル（自動計算・補足列・No.列・項目列）はグレー背景
      { sheetId, startRow: ROW.exchangeRate, endRow: ROW.exchangeRate, startCol: COL.value, endCol: COL.value, format: { backgroundColor: { red: 0.95, green: 0.95, blue: 0.95 } } },
      { sheetId, startRow: ROW.jpyAmount, endRow: ROW.jpyAmount, startCol: COL.value, endCol: COL.company3, format: { backgroundColor: { red: 0.95, green: 0.95, blue: 0.95 } } },
      { sheetId, startRow: ROW.currency, endRow: ROW.selectReason, startCol: COL.item, endCol: COL.item, format: { backgroundColor: { red: 0.97, green: 0.97, blue: 0.97 } } },
      { sheetId, startRow: ROW.currency, endRow: ROW.purchaseWithoutOutsourcing, startCol: COL.note, endCol: COL.note, format: { backgroundColor: { red: 0.97, green: 0.97, blue: 0.97 } } },
      { sheetId, startRow: ROW.sectionSelectReason, endRow: ROW.selectReason, startCol: COL.note, endCol: COL.note, format: { backgroundColor: { red: 0.97, green: 0.97, blue: 0.97 } } },
      { sheetId, startRow: ROW.companyName, endRow: ROW.companyName, startCol: COL.superiority, endCol: COL.superiority, format: { backgroundColor: { red: 0.95, green: 0.95, blue: 0.95 } } },
    ]);

    // 行高（自由記述行を拡大）
    await googleAPI.setRowHeight(spreadsheetId, sheetId, ROW.inHouseReviewNoReason - 1, ROW.inHouseReviewNoReason, 60);
    await googleAPI.setRowHeight(spreadsheetId, sheetId, ROW.competitiveEstimateNoReason - 1, ROW.competitiveEstimateNoReason, 60);
    await googleAPI.setRowHeight(spreadsheetId, sheetId, ROW.distributorReason - 1, ROW.distributorReason, 60);
    await googleAPI.setRowHeight(spreadsheetId, sheetId, ROW.negotiationBackground - 1, ROW.negotiationBackground, 60);
    await googleAPI.setRowHeight(spreadsheetId, sheetId, ROW.paymentBackground - 1, ROW.paymentBackground, 60);
    await googleAPI.setRowHeight(spreadsheetId, sheetId, ROW.ourProductUsageProductName - 1, ROW.ourProductUsageProductName, 60);
    await googleAPI.setRowHeight(spreadsheetId, sheetId, ROW.purchaseWithoutOutsourcing - 1, ROW.purchaseWithoutOutsourcing, 60);
    await googleAPI.setRowHeight(spreadsheetId, sheetId, ROW.selectReason - 1, ROW.selectReason, 100);

    // プルダウン（ONE_OF_LIST）
    const namesOf = (obj: Record<string, { name: string }>) => Object.values(obj).map(v => v.name);
    await googleAPI.spreadSheetSetDataValidation(spreadsheetId, [
      { sheetId, startRow: ROW.currency, endRow: ROW.currency, startCol: COL.value, endCol: COL.value, values: params.currencyData.map(c => c.currencyName ?? "") },
      { sheetId, startRow: ROW.equipmentType, endRow: ROW.equipmentType, startCol: COL.value, endCol: COL.value, values: namesOf(equipmentType) },
      { sheetId, startRow: ROW.reverseAuction, endRow: ROW.reverseAuction, startCol: COL.value, endCol: COL.value, values: namesOf(reverseAuction) },
      { sheetId, startRow: ROW.inHouseReview, endRow: ROW.inHouseReview, startCol: COL.value, endCol: COL.value, values: namesOf(inHouseReview) },
      { sheetId, startRow: ROW.competitiveEstimate, endRow: ROW.competitiveEstimate, startCol: COL.value, endCol: COL.value, values: namesOf(competitiveEstimate) },
      { sheetId, startRow: ROW.pastPerformance, endRow: ROW.pastPerformance, startCol: COL.value, endCol: COL.value, values: namesOf(pastPerformance) },
      { sheetId, startRow: ROW.supplier, endRow: ROW.supplier, startCol: COL.value, endCol: COL.value, values: namesOf(supplier) },
      { sheetId, startRow: ROW.paymentType, endRow: ROW.paymentType, startCol: COL.value, endCol: COL.value, values: namesOf(paymentType) },
      { sheetId, startRow: ROW.procurementApproval, endRow: ROW.procurementApproval, startCol: COL.value, endCol: COL.value, values: namesOf(procurementApproval) },
      { sheetId, startRow: ROW.ourProductUsage, endRow: ROW.ourProductUsage, startCol: COL.value, endCol: COL.value, values: namesOf(ourProductUsageStatus) },
      { sheetId, startRow: ROW.quality, endRow: ROW.quality, startCol: COL.value, endCol: COL.company3, values: namesOf(equipmentComparisonItems.quality) },
      { sheetId, startRow: ROW.system, endRow: ROW.system, startCol: COL.value, endCol: COL.company3, values: namesOf(equipmentComparisonItems.system) },
      { sheetId, startRow: ROW.repeat, endRow: ROW.repeat, startCol: COL.value, endCol: COL.company3, values: namesOf(equipmentComparisonItems.repeat) },
    ]);

    // 優位性の動的プルダウン（ONE_OF_RANGE、会社名セルC28:E28を参照）
    await googleAPI.spreadSheetSetRangeValidation(spreadsheetId, [
      {
        sheetId,
        startRow: ROW.basePrice,
        endRow: ROW.issue,
        startCol: COL.superiority,
        endCol: COL.superiority,
        sourceRange: `${GoogleAPI.DEFAULT_SHEET_NAME}!$C$${ROW.companyName}:$E$${ROW.companyName}`,
      },
    ]);

    // 日付入力規則
    const dateHelp = i18n.t(`${T_PREFIX}.validation.dateRangeError`, { start: formatDateByLocale(ValidDateRangeStart), end: formatDateByLocale(ValidDateRangeEnd) });
    const validDateRangeStartIso = ValidDateRangeStart.toISOString().slice(0, 10);
    const validDateRangeEndIso = ValidDateRangeEnd.toISOString().slice(0, 10);
    await googleAPI.spreadSheetSetDateValidation(spreadsheetId, [
      { sheetId, startRow: ROW.purchaseDate, endRow: ROW.purchaseDate, startCol: COL.value, endCol: COL.value, startDate: validDateRangeStartIso, endDate: validDateRangeEndIso, helpText: dateHelp },
      { sheetId, startRow: ROW.orderDate, endRow: ROW.orderDate, startCol: COL.value, endCol: COL.value, startDate: validDateRangeStartIso, endDate: validDateRangeEndIso, helpText: dateHelp },
    ]);
    await googleAPI.spreadSheetSetCellFormat(spreadsheetId, [
      { sheetId, startRow: ROW.purchaseDate, endRow: ROW.purchaseDate, startCol: COL.value, endCol: COL.value, format: { numberFormat: { type: "DATE", pattern: "yyyy-mm-dd" } } },
      { sheetId, startRow: ROW.orderDate, endRow: ROW.orderDate, startCol: COL.value, endCol: COL.value, format: { numberFormat: { type: "DATE", pattern: "yyyy-mm-dd" } } },
    ]);

    // 数値入力規則
    await googleAPI.spreadSheetSetNumberValidation(spreadsheetId, [
      { sheetId, startRow: ROW.purchasePrice, endRow: ROW.purchasePrice, startCol: COL.value, endCol: COL.value, min: 0, max: 999999999999 },
      { sheetId, startRow: ROW.negotiationCount, endRow: ROW.negotiationCount, startCol: COL.value, endCol: COL.value, min: 0, max: 999999999999 },
      { sheetId, startRow: ROW.basePrice, endRow: ROW.basePrice, startCol: COL.value, endCol: COL.company3, min: 0, max: 999999999999 },
      { sheetId, startRow: ROW.unit, endRow: ROW.unit, startCol: COL.value, endCol: COL.company3, min: 0, max: 999999999999 },
      { sheetId, startRow: ROW.optionPrice, endRow: ROW.optionPrice, startCol: COL.value, endCol: COL.company3, min: 0, max: 999999999999 },
      { sheetId, startRow: ROW.totalPrice, endRow: ROW.totalPrice, startCol: COL.value, endCol: COL.company3, min: 0, max: 999999999999 },
      { sheetId, startRow: ROW.deadline, endRow: ROW.deadline, startCol: COL.value, endCol: COL.company3, min: 0, max: 999999999999 },
    ]);
    await googleAPI.spreadSheetSetCellFormat(spreadsheetId, [
      { sheetId, startRow: ROW.purchasePrice, endRow: ROW.purchasePrice, startCol: COL.value, endCol: COL.value, format: { numberFormat: { type: "NUMBER", pattern: "#,##0" } } },
      { sheetId, startRow: ROW.negotiationCount, endRow: ROW.negotiationCount, startCol: COL.value, endCol: COL.value, format: { numberFormat: { type: "NUMBER", pattern: "#,##0" } } },
      { sheetId, startRow: ROW.basePrice, endRow: ROW.basePrice, startCol: COL.value, endCol: COL.company3, format: { numberFormat: { type: "NUMBER", pattern: "#,##0" } } },
      { sheetId, startRow: ROW.unit, endRow: ROW.unit, startCol: COL.value, endCol: COL.company3, format: { numberFormat: { type: "NUMBER", pattern: "#,##0" } } },
      { sheetId, startRow: ROW.optionPrice, endRow: ROW.optionPrice, startCol: COL.value, endCol: COL.company3, format: { numberFormat: { type: "NUMBER", pattern: "#,##0" } } },
      { sheetId, startRow: ROW.totalPrice, endRow: ROW.totalPrice, startCol: COL.value, endCol: COL.company3, format: { numberFormat: { type: "NUMBER", pattern: "#,##0" } } },
      { sheetId, startRow: ROW.jpyAmount, endRow: ROW.jpyAmount, startCol: COL.value, endCol: COL.company3, format: { numberFormat: { type: "NUMBER", pattern: "#,##0" } } },
      { sheetId, startRow: ROW.deadline, endRow: ROW.deadline, startCol: COL.value, endCol: COL.company3, format: { numberFormat: { type: "NUMBER", pattern: "#,##0" } } },
    ]);

    // 条件付き書式（非該当行の自動グレーアウト）
    const grayBg = { red: 0.9, green: 0.9, blue: 0.9 };
    const grayText = { red: 0.6, green: 0.6, blue: 0.6 };
    await googleAPI.spreadSheetAddConditionalFormat(spreadsheetId, [
      { sheetId, startRow: ROW.inHouseReviewNoReason, endRow: ROW.inHouseReviewNoReason, startCol: 1, endCol: 6, customFormula: `=$C$${ROW.inHouseReview}<>"${inHouseReview.isFalse.name}"`, backgroundColor: grayBg, textColor: grayText },
      { sheetId, startRow: ROW.competitiveEstimateNoReason, endRow: ROW.competitiveEstimateNoReason, startCol: 1, endCol: 6, customFormula: `=$C$${ROW.competitiveEstimate}<>"${competitiveEstimate.isFalse.name}"`, backgroundColor: grayBg, textColor: grayText },
      { sheetId, startRow: ROW.purchaseDate, endRow: ROW.purchasePrice, startCol: 1, endCol: 6, customFormula: `=$C$${ROW.pastPerformance}<>"${pastPerformance.isTrue.name}"`, backgroundColor: grayBg, textColor: grayText },
      { sheetId, startRow: ROW.distributorReason, endRow: ROW.distributorReason, startCol: 1, endCol: 6, customFormula: `=$C$${ROW.supplier}<>"${supplier.distributor.name}"`, backgroundColor: grayBg, textColor: grayText },
      { sheetId, startRow: ROW.procurementApproval, endRow: ROW.procurementApproval, startCol: 1, endCol: 6, customFormula: `=$C$${ROW.paymentType}<>"${paymentType.isTrue.name}"`, backgroundColor: grayBg, textColor: grayText },
      { sheetId, startRow: ROW.ourProductUsageProductName, endRow: ROW.ourProductUsageProductName, startCol: 1, endCol: 6, customFormula: `=$C$${ROW.ourProductUsage}="${ourProductUsageStatus.notApplicable.name}"`, backgroundColor: grayBg, textColor: grayText },
      { sheetId, startRow: ROW.basePrice, endRow: ROW.issue, startCol: COL.company2, endCol: COL.company2, customFormula: `=$D$${ROW.companyName}=""`, backgroundColor: grayBg, textColor: grayText },
      { sheetId, startRow: ROW.basePrice, endRow: ROW.issue, startCol: COL.company3, endCol: COL.company3, customFormula: `=$E$${ROW.companyName}=""`, backgroundColor: grayBg, textColor: grayText },
    ]);

    // 保護範囲（警告のみ）：No.列、項目列、補足列、自動計算セル、見出し行
    await googleAPI.spreadSheetAddProtectedRange(spreadsheetId, [
      { sheetId, startRow: 1, endRow: LAST_ROW, startCol: COL.no, endCol: COL.no, warningDescription: "No.列は読み取りキーのため編集しないでください。" },
      { sheetId, startRow: 1, endRow: LAST_ROW, startCol: COL.item, endCol: COL.item, warningDescription: "項目列は編集しないでください。" },
      // 補足列（D列）は基本チェック項目ブロックのみ。比較表ブロック（27〜39行）ではD列は「会社2」の入力列のため対象外
      { sheetId, startRow: ROW.currency, endRow: ROW.purchaseWithoutOutsourcing, startCol: COL.note, endCol: COL.note, warningDescription: "補足列は編集しないでください。" },
      { sheetId, startRow: ROW.sectionSelectReason, endRow: ROW.selectReason, startCol: COL.note, endCol: COL.note, warningDescription: "補足列は編集しないでください。" },
      { sheetId, startRow: ROW.exchangeRate, endRow: ROW.exchangeRate, startCol: COL.value, endCol: COL.value, warningDescription: "自動計算セルです。" },
      { sheetId, startRow: ROW.jpyAmount, endRow: ROW.jpyAmount, startCol: COL.value, endCol: COL.company3, warningDescription: "自動計算セルです。" },
    ]);

    // A列（No.キー）を非表示化
    // await googleAPI.hideColumns(spreadsheetId, sheetId, 0, 1);
  }, [googleAPI, params.currencyData]);

  const config = useMemo<SpreadSheetSyncConfig<EstimateCheckListSpreadSheetData>>(() => ({
    folderPath: EstimateCheckListSpreadSheet.folderPath,
    fileName,
    sharedDriveName: null,
    recreateFile: true,
    useFixedSheetName: true,
    toSpreadSheetData: (data) => estimateCheckListToSpreadSheetData(data, params.equipmentName),
    fromSpreadSheetData: () => ({ formValue: null as any, currencyData: params.currencyData }),
    getRange: (sheetName) => `${sheetName}!A1:F${LAST_ROW}`,
    onAfterWrite,
  }), [fileName, params.equipmentName, params.projectId, params.currencyData, onAfterWrite]);

  const syncResult = useSpreadSheetSync(config, googleAPI, toast);

  /**
   * データ連携ボタン押下時の変換込みハンドラ。
   * useSpreadSheetSyncのhandleSyncDataはconfig.fromSpreadSheetDataの戻り値のみ返すため、
   * ここでは同じ範囲取得結果を直接変換し直してエラー情報も取得する。
   */
  const handleSyncDataWithConversion = useCallback(async (
    existingFormValue: EstimateCheckListFormType
  ): Promise<EstimateCheckListConversionResult | null> => {
    if (!syncResult.state.activeSpreadsheetId) return null;
    if (!await googleAPI.ensureValidToken()) {
      await googleAPI.signIn();
    }
    try {
      const range = config.getRange(GoogleAPI.DEFAULT_SHEET_NAME);
      const result = await googleAPI.spreadSheetGetValues(syncResult.state.activeSpreadsheetId, range);
      return spreadSheetDataToEstimateCheckList(result.values ?? [], existingFormValue, params.currencyData);
    } catch (error) {
      console.error("データ連携エラー:", error);
      toast({
        title: i18n.t('common.label.error'),
        description: i18n.t('common.message.syncFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return null;
    }
  }, [syncResult.state.activeSpreadsheetId, googleAPI, config, params.currencyData, toast]);

  return {
    ...syncResult,
    handleSyncDataWithConversion,
  };
};

export default useEstimateCheckListSpreadSheet;
